import { useMemo } from 'react';
import type { ExtractAtomValue } from 'jotai';
import type { Atom } from 'jotai';
import { atom, useAtomValue } from 'jotai';
import { selectAtom, unwrap } from 'jotai/utils';
import { currentThemeAtom } from '../../../common/theme/theme-atoms';
import type { AnnotationLabelT, AnnotationUnderUserReviewMapT } from '../../../common/types/types';
import { ANNOTATION_TYPE } from '../../labelling/constants';
import type { AnnotationStoreT, FrameStateT } from '../../labelling/types';
import type { AnnotationT } from '../../labelling/types/annotations.type';
import { VIDEO_FILTERS } from '../constants';
import { EMPTY_FRAME_MAP, EMPTY_FRAMES, EMPTY_METADATA_MAP } from '../constants/annotations';
import type { FramePayload, MetadataPayload } from '../studio-store/types';
import type { AnnotationFramesRecord, AnnotationFramesStatus, AnnotationMetadataRecord, AnnotationUIStateRecord } from '../types/annotation';
import { applyM4LabelColors } from '../utils/apply-m4-label-colors';
import { resolveAnnotationType } from '../utils/resolve-annotation-type';
import { frameImageUrlsAtom } from './frame-image-urls-atom';
import { isVideoLabelingM4EnabledAtom } from './m4-atoms';
import { annotationsAtom, videoFilterAtom } from './side-panel-atoms';
import { storeVersionAtom, studioStoreAtom } from './studio-store';
import { currentFrameAtom } from './video-playback-atoms';
const _annotationLabelLabelsAtom = atom<AnnotationLabelT[]>([]);
export const overriddenM4LabelColorTitlesAtom = atom<ReadonlySet<string>>(new Set<string>());
export const annotationLabelsAtom = atom((get, _set) => {
  const labels = get(_annotationLabelLabelsAtom);
  const isM4Enabled = get(isVideoLabelingM4EnabledAtom);
  const theme = get(currentThemeAtom);
  const colorizedLabels = isM4Enabled ? applyM4LabelColors(labels, theme, get(overriddenM4LabelColorTitlesAtom)) : labels;

  // Copying annotationLabels as sort mutates the array in place.
  return [...colorizedLabels].sort((label1, label2) => label1.title.localeCompare(label2.title));
}, (_get, set, args: AnnotationLabelT[]) => {
  set(_annotationLabelLabelsAtom, args);
});
export const annotationFramesAtom = atom<FrameStateT[]>([]);
export const mlAnnotationFramesAtom = atom<Map<number, AnnotationStoreT>>(new Map());
export const consensusAnnotationFramesAtom = atom<Map<number, AnnotationStoreT>>(new Map());
export const numFramesAtom = atom<number>(get => {
  return get(frameCountAtom);
});
export const annotationUnderUserReviewAtom = atom<AnnotationUnderUserReviewMapT>({});

// Hydrated by StudioAtomHydrator from inputData.
// Covers modern tasks (videoUrl present) and legacy tasks (multi-frame image sequence).
// In v2, only videoUrl-based tasks are supported; this fallback will be removed.
export const isVideoTaskAtom = atom<boolean>(false);

// Set via SET_FRAME_COUNT dispatch once media loads and real frame count is known.
export const frameCountAtom = atom<number>(0);

// Current frame's image URL derived from frameImageUrlsAtom — used as the imageUrl
// prop passed to StageScope for image/PDF labeling tasks (memory mode).
export const currentFrameImageUrlAtom = atom<string>(get => {
  const frameImageUrls = get(frameImageUrlsAtom);
  const currentFrame = get(currentFrameAtom);
  return frameImageUrls[currentFrame] ?? '';
});
export const imageUrlForFrameIndexAtomCreator = (index: number) => atom<string | null>(get => {
  const frameImageUrls = get(frameImageUrlsAtom);
  return frameImageUrls[index] ?? null;
});
export const useImageUrlForFrameIndex = (index: number) => {
  const imageUrlForFrameIndexAtom = useMemo(() => imageUrlForFrameIndexAtomCreator(index), [index]);
  return useAtomValue(imageUrlForFrameIndexAtom);
};
export const annotationMetadataForIdAtomCreator = (annotationId: string) => atom<MetadataPayload | undefined>(get => get(annotationMetadataAtom).get(annotationId));
export const useAnnotationMetadataForId = (annotationId: string) => {
  const metadataAtom = useMemo(() => annotationMetadataForIdAtomCreator(annotationId), [annotationId]);
  return useAtomValue(metadataAtom);
};
export const isShowAllAnnotationsVisibleAtom = atom<boolean>(get => {
  const videoFilter = get(videoFilterAtom);
  if (videoFilter === VIDEO_FILTERS.CURRENT_FRAME) {
    const currentFrameIds = get(currentFrameLoadedAnnotationsAtom);
    const uiState = get(annotationUIStateAtom);
    return [...currentFrameIds.keys()].every(id => uiState.get(id)?.hidden ?? false);
  }
  if (videoFilter === VIDEO_FILTERS.ALL) {
    const allAnnotations = get(annotationsAtom);
    return allAnnotations.every(annotation => annotation.hidden);
  }
  return false;
});

// --- StudioStore-backed atoms ---

export const annotationUIStateAtom = atom<AnnotationUIStateRecord>(new Map());
export const mlAnnotationUIStateAtom = atom<AnnotationUIStateRecord>(new Map());

// Internal — drives loadedAnnotationFramesAtom and loadedAnnotationFramesStatusAtom. Not exported.
const asyncAnnotationFramesAtom: Atom<Promise<AnnotationFramesRecord>> = atom(async get => {
  // storeVersionAtom is read to subscribe to write operations: any mutation to StudioStore's
  // frame cache increments the version, causing this atom to re-evaluate and serve fresh data.
  get(storeVersionAtom);
  const studioStore = get(studioStoreAtom);
  if (!studioStore) {
    return EMPTY_FRAMES;
  }
  const currentFrame = get(currentFrameAtom);
  try {
    return studioStore.getAnnotationFrames(currentFrame);
  } catch {
    return EMPTY_FRAMES;
  }
});

// Always-synchronous view of annotation frames. Returns previous value while an async
// chunk load is in flight (windowed mode), so canvas and command factories never block.
export const loadedAnnotationFramesAtom = unwrap(asyncAnnotationFramesAtom, prev => prev ?? EMPTY_FRAMES);

// Returns 'LOADING' while a chunk fetch is in flight, 'HAS_DATA' once resolved.
// Used to block annotation interaction during windowed chunk loads.
const _asyncFramesStatusAtom = unwrap(asyncAnnotationFramesAtom, () => ('LOADING' as const));
export const loadedAnnotationFramesStatusAtom = atom<AnnotationFramesStatus>(get => {
  const result = get(_asyncFramesStatusAtom);
  if (result === 'LOADING') {
    return 'LOADING';
  }
  return 'HAS_DATA';
});
export const annotationMetadataAtom = atom<AnnotationMetadataRecord>(get => {
  // storeVersionAtom subscribed so metadata reflects any write operation on StudioStore.
  get(storeVersionAtom);
  return get(studioStoreAtom)?.getAnnotationMetadata() ?? EMPTY_METADATA_MAP;
});
export type AnnotationAtomHydrationList = [typeof annotationLabelsAtom, ExtractAtomValue<typeof annotationLabelsAtom>] | [typeof annotationFramesAtom, ExtractAtomValue<typeof annotationFramesAtom>] | [typeof annotationUnderUserReviewAtom, ExtractAtomValue<typeof annotationUnderUserReviewAtom>] | [typeof mlAnnotationFramesAtom, ExtractAtomValue<typeof mlAnnotationFramesAtom>] | [typeof mlAnnotationUIStateAtom, ExtractAtomValue<typeof mlAnnotationUIStateAtom>] | [typeof consensusAnnotationFramesAtom, ExtractAtomValue<typeof consensusAnnotationFramesAtom>] | [typeof isVideoTaskAtom, ExtractAtomValue<typeof isVideoTaskAtom>] | [typeof frameCountAtom, ExtractAtomValue<typeof frameCountAtom>];

// Derived atom for the current frame's annotation map from StudioStore.
// Returns a stable empty Map reference when the frame is not loaded.
export const currentFrameLoadedAnnotationsAtom = atom<Map<string, FramePayload>>(get => {
  const currentFrame = get(currentFrameAtom);
  const loadedFrames = get(loadedAnnotationFramesAtom);
  return loadedFrames.get(currentFrame) ?? EMPTY_FRAME_MAP;
});

// Internal: recomputes the full annotationId→label map on every metadata/label change.
const _rawAnnotationIdToLabelMapAtom = atom<Map<string, AnnotationLabelT>>(get => {
  const metadata = get(annotationMetadataAtom);
  const annotationLabels = get(annotationLabelsAtom);
  const map = new Map<string, AnnotationLabelT>();
  for (const [annotationId, m] of metadata) {
    const label = annotationLabels.find(l => l.title === m.labelId);
    if (label) {
      map.set(annotationId, label);
    }
  }
  return map;
});

// Selectors should be stable references to prevent unnecessary re-renders in consumers.
const _rawAnnotationIdToLabelMapSelector = (map: Map<string, AnnotationLabelT>) => map;
const labelMapsEqual = (a: Map<string, AnnotationLabelT>, b: Map<string, AnnotationLabelT>): boolean => {
  if (a.size !== b.size) {
    return false;
  }
  for (const [id, label] of b) {
    if (a.get(id) !== label) {
      return false;
    }
  }
  return true;
};

// Maps each annotationId to its full AnnotationLabelT.
// Returns the same Map reference when annotation IDs and label objects are unchanged,
// preventing unnecessary re-renders in consumers.
export const annotationIdToLabelMapAtom = selectAtom(_rawAnnotationIdToLabelMapAtom, _rawAnnotationIdToLabelMapSelector, labelMapsEqual);

// AnnotationStoreT for the current frame — single source of truth for the canvas.
//
// Reconstructs each annotation from StudioStore atoms — geometry from
// currentFrameLoadedAnnotationsAtom, label/answers/type from annotationMetadataAtom.
// Works for both windowed mode (lazy window) and memory mode (V1-Full, all frames resident).
// Only annotations in the current frame are included (O(annotations in frame)).
export const canvasAnnotationStoreAtom = atom<AnnotationStoreT>(get => {
  const loadedAnnotations = get(currentFrameLoadedAnnotationsAtom);
  const metadata = get(annotationMetadataAtom);
  const annotationIdToLabelMap = get(annotationIdToLabelMapAtom);
  const uiState = get(annotationUIStateAtom);
  const annotationStore: AnnotationStoreT = {};
  for (const [annotationId, framePayload] of loadedAnnotations) {
    const meta = metadata.get(annotationId);
    if (!meta) {
      continue;
    }
    const annotationLabel = annotationIdToLabelMap.get(annotationId);
    if (!annotationLabel) {
      continue;
    }
    const ui = uiState.get(annotationId);

    // A label's configured tool is fixed at task-setup time and can't disagree with its own
    // geometry, unlike meta.annotationType (sourced from the backend's annotationSummary),
    // which can come back stale/wrong for a given annotation — prefer the label's tool.
    const resolvedAnnotationType = resolveAnnotationType(annotationLabel, meta.annotationType);

    // Annotation of time frame classification is not rendered in stage.
    if (resolvedAnnotationType === ANNOTATION_TYPE.FRAME_CLASSIFICATION) {
      continue;
    }
    if (framePayload.geometry === null) {
      continue;
    }
    const annotationType = (resolvedAnnotationType as Exclude<AnnotationT['annotationType'], 'FRAME_CLASSIFICATION'>);

    // @ts-expect-error need runtime check to verify annotationType in meta matches data from geometry.
    annotationStore[annotationId] = {
      annotationType,
      data: framePayload.geometry,
      annotationLabel,
      metadata: {
        answers: meta.answers,
        originalId: meta.originalId,
        annotationSource: meta.annotationSource,
        consensusInfo: meta.consensusInfo
      },
      keyFrame: {
        isKeyFrame: framePayload.isKeyFrame,
        type: framePayload.keyFrameType,
        source: framePayload.keyFrameSource
      },
      hidden: ui?.hidden,
      locked: ui?.locked
    };
  }
  return annotationStore;
});
export const enhancedMLAnnotationFramesWithUIStateAtom = atom(get => {
  const mlAnnotationFrames = get(mlAnnotationFramesAtom);
  const uiState = get(mlAnnotationUIStateAtom);
  const canvasMLAnnotationFrames = new Map<number, AnnotationStoreT>();
  for (const [index, store] of mlAnnotationFrames) {
    const mlStore: AnnotationStoreT = {};
    for (const [id, mlAnnotation] of Object.entries(store)) {
      const mlUIState = uiState.get(id);
      if (!mlUIState) {
        mlStore[id] = mlAnnotation;
        continue;
      }
      mlStore[id] = {
        ...mlAnnotation,
        hidden: mlUIState.hidden,
        locked: mlUIState.locked
      };
    }
    canvasMLAnnotationFrames.set(index, mlStore);
  }
  return canvasMLAnnotationFrames;
});